import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { errorMessage, getMyNavSettings, saveMyNavSettings } from '../lib/api'
import { DEFAULT_NAV_SETTINGS } from '../lib/nav-items'
import type { NavSettings } from '../types'
import { sessionKeys } from './session'

const navSettingsKey = (userId: string) => [...sessionKeys.all, 'nav-settings', userId] as const

const navSettingsQuery = (userId: string) =>
	queryOptions({ queryKey: navSettingsKey(userId), queryFn: getMyNavSettings, enabled: userId !== '' })

export function useNavSettings(userId: string): NavSettings {
	return useQuery(navSettingsQuery(userId)).data ?? DEFAULT_NAV_SETTINGS
}

export function useSaveNavSettings(userId: string) {
	const queryClient = useQueryClient()
	const queryKey = navSettingsKey(userId)
	return useMutation({
		mutationFn: saveMyNavSettings,
		onMutate: async (settings: NavSettings) => {
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData<NavSettings>(queryKey)
			queryClient.setQueryData(queryKey, settings)
			return previous
		},
		onError: (error, _settings, previous) => {
			queryClient.setQueryData(queryKey, previous)
			toast.error(errorMessage(error, 'Navigation could not be saved'))
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey }),
	})
}
